import {
  CARD_TYPE_IMAGE_ACCEPTED_MIME_TYPES,
  CARD_TYPE_IMAGE_MAX_BYTES,
  CARD_TYPE_IMAGE_MAX_DIMENSION,
} from 'constants/cardTypeMessageTemplates';

export type TCardTypeImageValidationError =
  | 'invalidType'
  | 'tooLarge'
  | 'decodeFailed'
  | 'dimensionsTooLarge';

export type TCardTypeImageMimeType =
  (typeof CARD_TYPE_IMAGE_ACCEPTED_MIME_TYPES)[number];

export type TCardTypeImageValidationResult =
  | {
      ok: true;
      contentType: TCardTypeImageMimeType;
    }
  | {
      ok: false;
      error: TCardTypeImageValidationError;
    };

type TImageDimensions = {
  width: number;
  height: number;
};

type TReadImageDimensions = (file: File) => Promise<TImageDimensions>;
type TReadImageHeader = (file: File) => Promise<Uint8Array>;

type TCardTypeImageValidationOptions = {
  readDimensions?: TReadImageDimensions;
  readHeader?: TReadImageHeader;
};

const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
const JPEG_SIGNATURE = [0xff, 0xd8, 0xff];
const CARD_TYPE_IMAGE_HEADER_BYTES = PNG_SIGNATURE.length;

function startsWithBytes(bytes: Uint8Array, signature: number[]): boolean {
  return (
    bytes.length >= signature.length &&
    signature.every((value, index) => bytes[index] === value)
  );
}

export function detectCardTypeImageMimeType(
  bytes: Uint8Array,
): TCardTypeImageMimeType | undefined {
  if (startsWithBytes(bytes, PNG_SIGNATURE)) return 'image/png';
  if (startsWithBytes(bytes, JPEG_SIGNATURE)) return 'image/jpeg';
  return undefined;
}

export async function readCardTypeImageHeader(
  file: File,
): Promise<Uint8Array> {
  const buffer = await file.slice(0, CARD_TYPE_IMAGE_HEADER_BYTES).arrayBuffer();
  return new Uint8Array(buffer);
}

export function readCardTypeImageDimensions(
  file: File,
): Promise<TImageDimensions> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();

    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve({
        width: image.naturalWidth,
        height: image.naturalHeight,
      });
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to decode card image'));
    };
    image.src = url;
  });
}

export async function validateCardTypeImageFile(
  file: File,
  {
    readDimensions = readCardTypeImageDimensions,
    readHeader = readCardTypeImageHeader,
  }: TCardTypeImageValidationOptions = {},
): Promise<TCardTypeImageValidationResult> {
  const declaredMimeType = file.type as TCardTypeImageMimeType | '';
  if (
    declaredMimeType &&
    !CARD_TYPE_IMAGE_ACCEPTED_MIME_TYPES.includes(declaredMimeType)
  ) {
    return { ok: false, error: 'invalidType' };
  }

  if (file.size > CARD_TYPE_IMAGE_MAX_BYTES) {
    return { ok: false, error: 'tooLarge' };
  }

  let detectedMimeType: TCardTypeImageMimeType | undefined;
  try {
    detectedMimeType = detectCardTypeImageMimeType(await readHeader(file));
  } catch {
    return { ok: false, error: 'decodeFailed' };
  }
  if (
    !detectedMimeType ||
    (declaredMimeType && detectedMimeType !== declaredMimeType)
  ) {
    return { ok: false, error: 'invalidType' };
  }

  let dimensions: TImageDimensions;
  try {
    dimensions = await readDimensions(file);
  } catch {
    return { ok: false, error: 'decodeFailed' };
  }

  if (
    dimensions.width > CARD_TYPE_IMAGE_MAX_DIMENSION ||
    dimensions.height > CARD_TYPE_IMAGE_MAX_DIMENSION
  ) {
    return { ok: false, error: 'dimensionsTooLarge' };
  }

  return { ok: true, contentType: detectedMimeType };
}
